import React from 'react';
import { Link } from 'react-router-dom';
import LegalLayout, { LegalSection, LegalDisclaimer, LegalLastUpdated } from '../LegalLayout';
import { APP_NAME } from '../../config/branding';
import { LEGAL_ENTITY } from '../legalConstants';

export default function AcceptableUsePage() {
  return (
    <LegalLayout
      title="Acceptable Use & Misuse"
      subtitle={`Permitted use of ${APP_NAME} and our approach to user-generated content and liability.`}
    >
      <LegalLastUpdated />

      <LegalSection title="Purpose">
        <p>
          This policy describes how {APP_NAME} may be used and clarifies the limits of our responsibility for content
          created and shared by users. It supplements our{' '}
          <Link to="/terms" className="legal-inline-link">Terms & Conditions</Link> and{' '}
          <Link to="/legal/code-of-conduct" className="legal-inline-link">Code of Conduct</Link>.
        </p>
      </LegalSection>

      <LegalSection title="Permitted use">
        <ul>
          <li>Participating in discussions, clubs, and stories for personal, educational, or community purposes</li>
          <li>Sharing original content you have the right to distribute</li>
          <li>Sharing third-party content with proper attribution and within copyright and fair use limits</li>
          <li>Using personal and social profiles as intended — keeping identities separate and authentic</li>
          <li>Reporting abuse through official channels</li>
        </ul>
      </LegalSection>

      <LegalSection title="Prohibited misuse">
        <ul>
          <li>Illegal activity or promotion of illegal acts</li>
          <li>Intellectual property infringement at scale or after notice</li>
          <li>Automated scraping, bulk harvesting, or circumventing rate limits</li>
          <li>Creating accounts to evade bans or manipulate votes and visibility</li>
          <li>Distributing unsolicited commercial messages (spam)</li>
          <li>Uploading malicious files or attempting unauthorized access</li>
          <li>Using the platform to store or transmit content unrelated to community participation at abusive volume</li>
        </ul>
      </LegalSection>

      <LegalSection title="User-generated content — limitation of responsibility">
        <div className="legal-callout">
          <p className="legal-callout-title">Important</p>
          <p style={{ margin: 0 }}>
            {LEGAL_ENTITY} does not create, endorse, or guarantee the accuracy of posts, comments, stories, media, or
            messages submitted by users. Opinions expressed by members are their own.
          </p>
        </div>
        <p>
          We are not liable for user-generated content, including text, images, videos, PDFs, links, or attachments shared
          on {APP_NAME}. Users are solely responsible for what they publish under their personal or social identity.
        </p>
        <p>
          That said, when content is reported and verified to violate our policies or applicable law, we may take action
          — including removing posts, comments, media, club content, or stories; restricting accounts; or cooperating
          with authorities. Reporting does not guarantee removal; each report is reviewed according to our guidelines
          and available resources.
        </p>
      </LegalSection>

      <LegalSection title="How to report misuse">
        <p>
          Use in-app reporting where available, or visit{' '}
          <Link to="/legal/safety" className="legal-inline-link">Safety & Reporting</Link>. Include links, usernames,
          and a clear description. For urgent safety matters involving imminent harm, contact local emergency services
          first, then notify us at{' '}
          {/* <a href={`mailto:${SUPPORT_EMAIL}`} className="legal-inline-link">{SUPPORT_EMAIL}</a>. */}
          <Link to="/contact" className="legal-inline-link">Contact</Link> form.
        </p>
      </LegalSection>

      <LegalSection title="Repeat violations">
        <p>
          Accounts with repeated or severe violations may be permanently banned. Attempts to return using alternate
          accounts may result in broader restrictions, including IP or device-level measures where legally permitted.
        </p>
      </LegalSection>

      <LegalDisclaimer />
    </LegalLayout>
  );
}
